"use client";

import { useEffect } from "react";

import { isPlatformHost } from "@/lib/platformHost";
import {
  PLATFORM_PWA_SCOPE,
  PLATFORM_SW_URL,
  isPlatformPwaPath,
  shouldDeleteCacheOnPlatformHost,
  shouldUnregisterOnPlatformHost,
} from "@/lib/platformPwa";

// Versi build di-inline saat build (next.config `env`). Dipakai sebagai query
// "?v=" pada scriptURL → deploy baru = scriptURL baru = update SW terdeteksi
// otomatis, dan SW memakai versi ini untuk menamai cache (anti-"lengket").
const SW_VERSION = process.env.NEXT_PUBLIC_SW_VERSION || "dev";

/**
 * Mendaftarkan SATU service worker (`/sw.js`, scope "/") untuk KEDUA PWA
 * (Pelanggan & Admin). Dimount di `Providers` (global) → jalan di storefront
 * maupun `/admin`. Render null — tanpa DOM. Di host platform yang didaftarkan
 * SW panel platform (`/sw-platform.js`), dan hanya di halaman `/platform/...`.
 *
 * Hanya aktif di produksi agar tidak mengganggu hot-reload saat `next dev`.
 */
export function ServiceWorkerRegister() {
  useEffect(() => {
    if (typeof window === "undefined") return;
    if (typeof navigator === "undefined" || !("serviceWorker" in navigator)) {
      return;
    }

    // Origin platform (kelolify.com) TIDAK boleh punya service worker toko.
    // Sebelum halaman platform ada, origin ini menyajikan toko penuh, jadi SW
    // PWA toko sudah terlanjur ter-register di sana dan akan terus menyajikan
    // shell toko dari cache meski server sudah mengirim halaman platform.
    // Service worker hidup PER-ORIGIN, jadi hanya kode yang jalan di origin itu
    // yang bisa mencabutnya — karena itu unregister + buang cache di sini.
    // localhost bukan platform host, jadi dev tidak terpengaruh.
    //
    // PENGECUALIAN: SW PANEL PLATFORM (`/sw-platform.js`, scope "/platform/",
    // `platformPwa.ts`) dan cache berawalan miliknya dibiarkan hidup — dulu
    // ikut dicabut di setiap muat halaman, sehingga panel tak pernah bisa
    // dipasang maupun menerima push.
    const platformHost = isPlatformHost(window.location.hostname);
    if (platformHost) {
      const origin = window.location.origin;
      void navigator.serviceWorker
        .getRegistrations()
        .then((regs) =>
          Promise.all(
            regs
              .filter((reg) => shouldUnregisterOnPlatformHost(reg.scope, origin))
              .map((reg) => reg.unregister())
          )
        )
        .catch(() => {});
      if (typeof caches !== "undefined") {
        void caches
          .keys()
          .then((keys) =>
            Promise.all(
              keys.filter(shouldDeleteCacheOnPlatformHost).map((key) => caches.delete(key))
            )
          )
          .catch(() => {});
      }
      // Situs jualan & katalog reseller di origin ini tetap TANPA SW; hanya
      // halaman `/platform/...` yang mendaftarkan SW panel.
      if (!isPlatformPwaPath(window.location.pathname)) return;
    }

    if (process.env.NODE_ENV !== "production") return;
    const [scriptUrl, scope] = platformHost
      ? [PLATFORM_SW_URL, PLATFORM_PWA_SCOPE]
      : ["/sw.js", "/"];
    const register = () => {
      navigator.serviceWorker
        .register(`${scriptUrl}?v=${encodeURIComponent(SW_VERSION)}`, { scope })
        .catch((err) => {
          // Kegagalan SW tak boleh menggagalkan halaman.
          // eslint-disable-next-line no-console
          console.error("Service worker registration failed:", err);
        });
    };
    if (document.readyState === "complete") {
      register();
      return;
    }
    window.addEventListener("load", register, { once: true });
    return () => window.removeEventListener("load", register);
  }, []);

  return null;
}
